import { Model } from '../types';
import './ModelDetail.css';

interface Props {
  model: Model;
  onBack: () => void;
}

// Telegram WebApp from window
const WebApp = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;

const BENCHMARK_ORDER = [
  'chatbot_arena_elo', 'hf_average', 'mmlu_pro', 'math',
  'gpqa', 'ifeval', 'bbh', 'musr', 'swe_bench_verified',
];

const BENCHMARK_META: Record<string, { label: string; max: number; unit: string }> = {
  chatbot_arena_elo:  { label: '🏆 Chatbot Arena ELO',  max: 1600, unit: ' ELO' },
  hf_average:         { label: '📊 HF Average',           max: 100,  unit: '%' },
  mmlu_pro:           { label: '🧠 MMLU-Pro',             max: 100,  unit: '%' },
  math:               { label: '🔢 MATH',                 max: 100,  unit: '%' },
  gpqa:               { label: '🔬 GPQA',                 max: 100,  unit: '%' },
  ifeval:             { label: '📋 IFEval',               max: 100,  unit: '%' },
  bbh:                { label: '💡 BBH',                  max: 100,  unit: '%' },
  musr:               { label: '🔗 MUSR',                 max: 100,  unit: '%' },
  swe_bench_verified: { label: '💻 SWE-bench Verified',   max: 100,  unit: '%' },
};

const CHANNEL_ICONS: Record<string, string> = {
  api: '⚡',
  web_ui: '🌐',
  ide_plugin: '💻',
  mobile_app: '📱',
};

export function ModelDetail({ model, onBack }: Props) {
  const pricing = model.pricing?.[0];

  const handleUseModel = () => {
    // Copy model ID to clipboard
    navigator.clipboard.writeText(model.canonicalId);
    WebApp?.showAlert('Model ID copied to clipboard!');
  };

  return (
    <div className="model-detail">
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
      </div>

      <div className="detail-content">
        <h1 className="detail-title">{model.name}</h1>
        <p className="detail-developer">{model.developer}</p>

        {model.description && (
          <p className="detail-description">{model.description}</p>
        )}

        <div className="detail-section">
          <h3>💰 Pricing</h3>
          <div className="pricing-info">
            {pricing?.freeTier ? (
              <div className="pricing-row free">
                <span>Free Tier</span>
                <span>✅ Available</span>
              </div>
            ) : (
              <div className="pricing-row">
                <span>Input</span>
                <span>${pricing?.inputPricePerMillion?.toFixed(2) || 'N/A'}/1M tokens</span>
              </div>
            )}

            {pricing?.requiresDeposit && (
              <div className="pricing-row warning">
                <span>⚠️ Requires Deposit</span>
                <span>${pricing.minDepositAmount}+</span>
              </div>
            )}

            {pricing?.dataUsedForTraining && (
              <div className="pricing-row alert">
                <span>⚠️ Data used for training</span>
              </div>
            )}

            <div className="pricing-row">
              <span>Privacy Level</span>
              <span className={`privacy-${pricing?.privacyLevel}`}>
                {pricing?.privacyLevel || 'unknown'}
              </span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>⚙️ Specifications</h3>
          <div className="specs-grid">
            {model.contextWindow && (
              <div className="spec-item">
                <span className="spec-label">Context Window</span>
                <span className="spec-value">{model.contextWindow.toLocaleString()} tokens</span>
              </div>
            )}
            {model.maxOutputTokens && (
              <div className="spec-item">
                <span className="spec-label">Max Output</span>
                <span className="spec-value">{model.maxOutputTokens.toLocaleString()} tokens</span>
              </div>
            )}
            <div className="spec-item">
              <span className="spec-label">Architecture</span>
              <span className="spec-value">{model.architecture}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Open Source</span>
              <span className="spec-value">{model.isOpenSource ? 'Yes ✅' : 'No ❌'}</span>
            </div>
          </div>
        </div>

        {model.capabilities && model.capabilities.length > 0 && (
          <div className="detail-section">
            <h3>🛠️ Capabilities</h3>
            <div className="capabilities-list">
              {model.capabilities.map((cap, idx) => (
                <div key={idx} className="capability-item">
                  <span className="capability-name">{cap.modality}</span>
                  <div className="capability-features">
                    {cap.streamingSupport && <span className="feature">stream</span>}
                    {cap.jsonModeSupport && <span className="feature">json</span>}
                    {cap.toolUseSupport && <span className="feature">tools</span>}
                    {cap.visionSupport && <span className="feature">vision</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {model.quickMetrics && (
          <div className="detail-section">
            <h3>📊 Performance Ratings</h3>
            <div className="metrics-overall">
              <span className="overall-score">{(model.quickMetrics.overallScore * 2).toFixed(1)}</span>
              <span className="overall-label">/ 10 overall</span>
              {model.quickMetrics.bestFor.length > 0 && (
                <div className="best-for">
                  {model.quickMetrics.bestFor.map(tag => (
                    <span key={tag} className="best-for-tag">{tag.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="metrics-grid">
              {[
                { key: 'speed', label: 'Speed', icon: '⚡' },
                { key: 'coding', label: 'Coding', icon: '💻' },
                { key: 'reasoning', label: 'Reasoning', icon: '🧠' },
                { key: 'math', label: 'Math', icon: '🔢' },
                { key: 'toolUse', label: 'Tool Use', icon: '🛠️' },
                { key: 'creativity', label: 'Creativity', icon: '✍️' },
                { key: 'multilingual', label: 'Multilingual', icon: '🌍' },
                { key: 'contextUnderstanding', label: 'Long Context', icon: '📄' },
              ].map(({ key, label, icon }) => {
                const value = model.quickMetrics![key as keyof typeof model.quickMetrics] as number;
                return (
                  <div key={key} className="metric-item">
                    <span className="metric-label">{icon} {label}</span>
                    <div className="metric-bar-wrap">
                      <div className="metric-bar" style={{ width: `${value * 20}%` }} />
                    </div>
                    <span className="metric-value">{value * 2}/10</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {model.benchmarks && model.benchmarks.length > 0 && (
          <div className="detail-section">
            <h3>📐 Benchmarks</h3>
            <div className="benchmarks-list">
              {model.benchmarks
                .slice()
                .sort((a, b) => BENCHMARK_ORDER.indexOf(a.benchmark) - BENCHMARK_ORDER.indexOf(b.benchmark))
                .map(b => {
                  const meta = BENCHMARK_META[b.benchmark] || { label: b.benchmark, max: 100, unit: '' };
                  const isElo = b.benchmark === 'chatbot_arena_elo';
                  const pct = isElo
                    ? Math.max(0, Math.min(100, ((b.score - 800) / 800) * 100))
                    : Math.min(100, b.score);
                  return (
                    <div key={b.id} className="benchmark-item">
                      <div className="benchmark-header">
                        <span className="benchmark-name">{meta.label}</span>
                        <span className="benchmark-score">
                          {isElo ? Math.round(b.score) : b.score.toFixed(1)}{meta.unit}
                        </span>
                      </div>
                      <div className="benchmark-bar-wrap">
                        <div className="benchmark-bar" style={{ width: `${pct}%` }} />
                      </div>
                      {b.source && (
                        <span className="benchmark-source">
                          {b.url
                            ? <a href={b.url} target="_blank" rel="noopener noreferrer">{b.source}</a>
                            : b.source}
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {model.accessChannels && model.accessChannels.length > 0 && (
          <div className="detail-section">
            <h3>🚀 Where to Access</h3>
            <div className="channels-list">
              {model.accessChannels.map((ch) => (
                <div key={ch.id} className="channel-item">
                  <div className="channel-header">
                    <span className="channel-name">
                      {CHANNEL_ICONS[ch.channelType] || '🔗'} {ch.channelName}
                      {ch.isOfficial && <span className="official-badge">official</span>}
                    </span>
                    <div className="channel-badges">
                      {ch.freeTier && <span className="badge free">FREE</span>}
                      {ch.paidTier && !ch.freeTier && <span className="badge paid">PAID</span>}
                      {ch.reliability === 'high' && <span className="badge reliable">✓</span>}
                    </div>
                  </div>
                  {ch.freeTier && ch.freeLimit && (
                    <p className="channel-limit">{ch.freeLimit}</p>
                  )}
                  {ch.pricingNote && (
                    <p className="channel-price">{ch.pricingNote}</p>
                  )}
                  {ch.notes && (
                    <p className="channel-notes">{ch.notes}</p>
                  )}
                  {(ch.requestsPerDay || ch.requestsPerMinute || ch.tokensPerMinute) && (
                    <div className="channel-limits">
                      {ch.requestsPerDay && (
                        <span className="limit-chip">{ch.requestsPerDay.toLocaleString()} req/day</span>
                      )}
                      {ch.requestsPerMinute && (
                        <span className="limit-chip">{ch.requestsPerMinute} req/min</span>
                      )}
                      {ch.tokensPerMinute && (
                        <span className="limit-chip">{formatTokens(ch.tokensPerMinute)}/min</span>
                      )}
                    </div>
                  )}
                  {ch.limitNotes && (
                    <p className="channel-limit-notes">{ch.limitNotes}</p>
                  )}
                  {(ch.referralUrl || ch.url) && (
                    <a
                      className="channel-link"
                      href={ch.referralUrl || ch.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="detail-section">
          <h3>📝 Model ID</h3>
          <code className="model-id">{model.canonicalId}</code>
        </div>
      </div>

      <div className="detail-actions">
        <button className="use-btn" onClick={handleUseModel}>
          📋 Copy Model ID
        </button>
      </div>
    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M tok`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K tok`;
  return `${n} tok`;
}
